import type { Feature, Geometry } from "geojson";
import { GeoJsonLayer } from "deck.gl";
import { ObbAnnotation } from "../../api/Application";
import { DrawingModeContext, Mode, ModeVehicleParts } from "./ModeContext";
import { parseVehicleType, VehicleType } from "./VehicleType";
import { Pixel } from "../../common/Geography";

export interface Part {
    size: number; //size of the part
    name: string; //the name of the part
}

export interface FeatureCol {
    type:     "FeatureCollection";
    features: Feature<Geometry, FeatureProperties>[];
}

type FeatureProperties = {
    kind: "slice" | "arrow";
    id:   number;
    ind:  number;
    edit: boolean;
};

//colors for each part
const partColors: [number, number, number][] = [
    [ 255, 140,   0], //orange
    [ 255, 206,  84], //yellow
    [  80, 200, 120], //green
    [   0, 200, 200], //cyan
    [  64, 156, 255], //blue
    [ 180, 100, 220], //purple
];

const hd = "Hood";        //common to all vehicles
const ws = "Windshield";  //kind of common (except convertibles)
const cp = "Cockpit";     //only on convertibles
const cb = "Cabin";       //common to all but convertibles
const rw = "Rear Window"; //optional on a lot of types
const rd = "Rear Door";   //present on hatchbacks, suvs, and vans
const tr = "Trunk";       //only present on cars
const bd = "Bed";         //pickup truck bed
const configs: [ VehicleType, string[] ][] = [
    //cars
    [ VehicleType.Car,         [ hd, ws, cb, rw, tr   ] ], //coupes, sedans

    //suvs
    [ VehicleType.Suv,         [ hd, ws, cb           ] ], //suvs and vans have same part names
    [ VehicleType.Suv,         [ hd, ws, cb, rw       ] ], //suvs and vans have same part names
    [ VehicleType.Suv,         [ hd, ws, cb, rw, rd   ] ], //suvs and vans have same part names

    //vans
    [ VehicleType.Van,         [ hd, ws, cb           ] ], //suvs and vans have same part names
    [ VehicleType.Van,         [ hd, ws, cb, rw       ] ], //suvs and vans have same part names
    [ VehicleType.Van,         [ hd, ws, cb, rw, rd   ] ], //suvs and vans have same part names

    //pickups
    [ VehicleType.Pickup,      [ hd, ws, cb, bd       ] ], //sometimes trucks have have camper, not sure what to do
    [ VehicleType.Pickup,      [ hd, ws, cb           ] ], //sometimes trucks have have camper, not sure what to do

    //convertibles
    [ VehicleType.Convertible, [ hd, cp, tr           ] ], //sometimes the windshield isn't visible
    [ VehicleType.Convertible, [ hd, ws, cp, tr       ] ], //sometimes it is

    //hatchbacks
    [ VehicleType.Hatchback,   [ hd, ws, cb, rw       ] ], //rear window covers the back
    [ VehicleType.Hatchback,   [ hd, ws, cb, rw, rd   ] ], //the rear window covers part of the back
];

//gets the name for the part starting at index a
export function initPartMap() {
    //the number in the nested map is the number of points
    const map = new Map<VehicleType, Map<number, string[]>>();
    for( const [ vehicleType, names ] of configs ) {
        if( !map.has( vehicleType ) ) {
            map.set( vehicleType, new Map<number, string[]>() );
        }
        //the names.length - 1 is so that we can look up the configuration from the points passed in 
        map.get( vehicleType )!.set( names.length - 1, names );
    }
    return map;
}

export function getParts( ann: ObbAnnotation ): Part[] {
    if( !isSupported( ann ) ) { return []; }
    const type = parseVehicleType( ann.attributes.vehicleType! )!;
    const pts  = ann.attributes.parts ?? [];
    if( !isValid( type, pts.length ) ) { return []; }

    //we know this will return a cfg
    const cfg    = partsMap.get( type )!.get( pts.length )!;
    const allPts = [1, ...pts, 0];
    const parts: Part[] = [];
    for( let i = 0; i < allPts.length - 1; i++ ) { //-1 guarantees we won't go out of bounds
        const part = allPts[i] - allPts[i + 1];
        const name = cfg[i];
        parts.push( { size: part, name: name } );
    }
    return parts;
}

//does the vehicle have a valid type that supports annotation?
export function isSupported( ann: ObbAnnotation ): boolean {
    const str = ann.attributes?.vehicleType;
    if( str === null ) { return false; }
    
    const type = parseVehicleType( str );
    if( type === undefined ) { return false; }
    
    //if it has a cfg for its type it's supported
    return partsMap.has( type );
}

export function isValid( vehicleType: VehicleType, points: number ) {
    const cfgs = partsMap.get( vehicleType );
    if( cfgs === undefined ) { return false; }
    const match = cfgs.get( points );    
    if( match === undefined ) { return false; }
    return true;
}

export function hasConfigs( vehicleType: VehicleType ) {
    return partsMap.has( vehicleType );
}

export const partsMap = initPartMap();

/**
 * Project a world-space point (px, py) onto the length axis of an OBB annotation.
 * Returns a value in [0, 1] where 0 is the rear and 1 is the front of the vehicle.
 * The "length" axis follows the OBB's `width` dimension + `angle`.
 */
export function projectToVehicleLength(
    ann:         ObbAnnotation,
    worldPt:     [number, number],
    imageWidth:  number,
    imageHeight: number
): number {
    const cx = ann.x * imageWidth;
    const cy = ann.y * imageHeight;
    const lengthPx = ann.width * imageWidth; //front-to-rear length in pixels
    const rad = ( ann.angle * Math.PI ) / 180;
    const ux = Math.cos( rad );
    const uy = Math.sin( rad );

    //vector from center to point
    const dx = worldPt[0] - cx;
    const dy = worldPt[1] - cy;

    //project onto length axis
    const proj = dx * ux + dy * uy;  // in pixels, signed (-L/2 .. +L/2)
    const t = proj / lengthPx + 0.5; //normalize to 0..1
    return Math.max( 0, Math.min( 1, t ) );
}

/**
 * Build a quad (4 corners, world/pixel space) representing a slice of the vehicle
 * between normalized length values tStart..tEnd. The slice spans the full width
 * (OBB height) of the vehicle.
 */
function buildSliceQuad(
    ann: ObbAnnotation,
    tStart: number,
    tEnd: number,
    imageWidth: number,
    imageHeight: number
): Pixel[] {
    const cx  = ann.x        * imageWidth;
    const cy  = ann.y        * imageHeight;
    const L   = ann.width    * imageWidth;  //length
    const W   = ann.height   * imageHeight; //side-to-side width
    const rad = ( ann.angle * Math.PI ) / 180;
    const ux  = Math.cos( rad );
    const uy  = Math.sin( rad );
    //perpendicular (width) axis
    const px  = -uy;
    const py  =  ux;

    //convert t in [0,1] to signed offset along length axis
    const offStart = ( tStart - 0.5 ) * L;
    const offEnd   = ( tEnd   - 0.5 ) * L;
    const halfW    = W / 2;
    const p0: Pixel = [ cx + ux * offStart - px * halfW, cy + uy * offStart - py * halfW ] as Pixel;
    const p1: Pixel = [ cx + ux * offEnd   - px * halfW, cy + uy * offEnd   - py * halfW ] as Pixel;
    const p2: Pixel = [ cx + ux * offEnd   + px * halfW, cy + uy * offEnd   + py * halfW ] as Pixel;
    const p3: Pixel = [ cx + ux * offStart + px * halfW, cy + uy * offStart + py * halfW ] as Pixel;
    return [p0, p1, p2, p3];
}

//module-scope cache: only one segment-editing session is ever active at a time
export function buildGeoJsonLayer( data: FeatureCol ) {
    const colLen = partColors.length;
    return new GeoJsonLayer<FeatureProperties>( {
        id:                 "parts",
        data:               data,
        stroked:            true,
        filled:             true,
        getLineWidth:       0.25,
        lineWidthMinPixels: 2.00,
        
        getFillColor: f => {
            if( f.properties.kind === "arrow" ) { return [255, 255, 255, 220]; }
            const c = partColors[f.properties.ind % colLen];
            return [c[0], c[1], c[2], 0];
        },
        getLineColor: f => {
            if( f.properties.kind === "arrow" ) { return [0, 0, 0, 255]; }            
            const c = partColors[f.properties.ind % colLen];
            const alpha = f.properties.edit === true ? 128 : 200;
            return [c[0], c[1], c[2], alpha];
        },        
        pickable: false
    } );
}

interface DrawListItem {
    ann:       ObbAnnotation;
    pts:       number[];
    isEditing: boolean;
}

export function buildPartsFeatures( drawList: DrawListItem[], ctx: DrawingModeContext ): FeatureCol {
    const features: Feature<Geometry, FeatureProperties>[] = [];
    for( const { ann, pts, isEditing } of drawList ) {
        //build segment boundary list: [0, ...sorted points, 1]
        const sorted = [...pts].sort( ( a, b ) => a - b );
        const bounds = [0, ...sorted, 1];
        const last   = bounds.length - 1;
        const penult = bounds.length - 2;
        for( let i = 0; i < last; i++ ) {
            const a = bounds[i];
            const b = bounds[i + 1];
            if( b <= a ) { continue; }
            
            //clever trick to not overdraw onto each part
            const [p0, p1, p2, p3] = buildSliceQuad( ann, a, b, ctx.imageWidth, ctx.imageHeight );                        
            const openRing   = [p2, p3, p0, p1];
            const closedRing = [p2, p3, p0, p1, p2];            
            const ring = i !== penult ? openRing : closedRing; //close ring on last
            features.push( createPartFeature( ring, i, ann, isEditing ) );
        }

        //arrow only on the actively-edited vehicle, otherwise the screen fills with arrows
        if( isEditing ) {
            const arrow = buildFrontArrow( ann, ctx.imageWidth, ctx.imageHeight );
            features.push( createArrowFeature( arrow, ann ) );
        }
    }
    const res: FeatureCol = { type: "FeatureCollection", features };
    return res;
}

function createArrowFeature( arrow: Pixel[], ann: ObbAnnotation ): Feature<Geometry, FeatureProperties> {
    return {
        type: "Feature",
        geometry: { 
            type:        "Polygon",
            coordinates: [arrow]
        },
        properties: {
            kind:        "arrow",
            ind:         -1,
            id:          ann.obbAnnotationId,
            edit:        false,
        }
    };
}

//create MultiLineString
function createPartFeature( partGeo: Pixel[], index: number, ann: ObbAnnotation, editing: boolean ): Feature<Geometry, FeatureProperties> {
    return {
        type: "Feature",
        geometry:   {
            type:        "MultiLineString",
            coordinates: [partGeo]
        },
        properties: {
            kind: "slice",
            ind:  index,
            id:   ann.obbAnnotationId,
            edit: editing,
        }
    };
}

/**
 * Build a triangle sitting just past the front edge of the OBB, pointing
 * outward along the vehicle's length axis. Tip is at ~1.2 * L/2 from the
 * center; base spans the OBB's width at the front edge.
 */
function buildFrontArrow( ann: ObbAnnotation, imageWidth: number, imageHeight: number ): Pixel[] {
    const cx = ann.x * imageWidth;
    const cy = ann.y * imageHeight;
    const L  = ann.width  * imageWidth;
    const W  = ann.height * imageHeight;
    const rad = ( ann.angle * Math.PI ) / 180;
    const ux = Math.cos( rad );
    const uy = Math.sin( rad );
    const px = -uy;
    const py =  ux;

    //arrow geometry: scale base to ~60% of vehicle width, tip projects
    //forward by ~half the vehicle width (capped so it's always visible)
    const halfBase = W * 0.3;  //or whatever base width you want
    const gap      = 2;        //2 image pixels between OBB front and arrow base
    const tipOut   = W * 0.3;  //arrow length — tweak to taste
    const baseOff  = L / 2 + gap;
    const tipOff   = baseOff + tipOut;

    const baseL: Pixel = [ cx + ux * baseOff - px * halfBase, cy + uy * baseOff - py * halfBase ] as Pixel;
    const baseR: Pixel = [ cx + ux * baseOff + px * halfBase, cy + uy * baseOff + py * halfBase ] as Pixel;
    const tip:   Pixel = [ cx + ux * tipOff,                  cy + uy * tipOff                  ] as Pixel;
    return [ baseL, tip, baseR, baseL ]; //closed ring
}